import { toast } from 'sonner';

import { useCaptureError } from '@/lib/posthog/hooks/useCaptureError';
import { useTrackEvent } from '@/lib/posthog/hooks/useTrackEvent';
import { useCreateCandidateJobNoteMutation } from '@/modules/candidates/mutations/useCreateCandidateJobNoteMutation';
import { useCandidateJobNotesQuery } from '@/modules/candidates/queries/useCandidateJobNotesQuery';
import type {
  CandidateJobNoteType,
  NoteFormData,
} from '@/modules/notes/components/NoteForm';
import { NotesList } from '@/modules/notes/components/NotesList';
import type { components } from '@/types/apiSchema';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

import { CandidateJobNoteCard } from './CandidateJobNoteCard';

type CandidateJobNoteResponse =
  components['schemas']['CandidateJobNoteResponse'];

export type CandidateJobNote = CandidateJobNoteResponse;

export const candidateJobNoteTypeOptions: {
  value: CandidateJobNoteType;
  label: string;
}[] = [
  { value: 'Prescreen', label: 'Prescreen' },
  { value: 'General', label: 'General' },
];

interface CandidateJobNotesListProps {
  candidateJobId: string;
  defaultType?: CandidateJobNoteType;
  canEditNote?: (note: CandidateJobNoteResponse) => boolean;
  typeOptions?: { value: CandidateJobNoteType; label: string }[];
}

export const CandidateJobNotesList = ({
  candidateJobId,
  defaultType,
  canEditNote,
  typeOptions = candidateJobNoteTypeOptions,
}: CandidateJobNotesListProps) => {
  const { data: notes, isLoading } = useCandidateJobNotesQuery(candidateJobId);
  const createNoteMutation = useCreateCandidateJobNoteMutation();
  const trackEvent = useTrackEvent();
  const captureError = useCaptureError();
  const visibleNotes = notes?.filter((note) => note.type !== 'AiPrescreenRaw');

  const handleAddNote = (data: NoteFormData) => {
    const noteType = data.type as 'General' | 'Prescreen';

    createNoteMutation.mutate(
      {
        candidateJobId,
        body: {
          content: data.content,
          type: noteType,
        },
      },
      {
        onSuccess: () => {
          toast.success('Note added successfully');
        },
        onError: (error) => {
          const errorMessage = extractErrorMessage(error);

          // Surface the failure in analytics so intermittent server errors
          // (e.g. prescreen note polishing) are measurable instead of only
          // ever being reported by hand.
          trackEvent('CANDIDATE_JOB_NOTE_CREATE_FAILED', {
            candidateJobId,
            noteType,
            errorMessage,
          });
          captureError(error, {
            errorType: 'candidate-job-note-create-error',
            candidateJobId,
            noteType,
          });

          toast.error(`Failed to add note: ${errorMessage}`);
        },
      },
    );
  };

  return (
    <NotesList
      notes={visibleNotes}
      isLoading={isLoading}
      onAddNote={handleAddNote}
      isCreating={createNoteMutation.isPending}
      title="Notes"
      description="Keep track of important information about this candidate"
      typeOptions={typeOptions}
      defaultType={defaultType}
      renderNoteCard={(note) => (
        <CandidateJobNoteCard
          note={note}
          candidateJobId={candidateJobId}
          canEdit={canEditNote ? canEditNote(note) : true}
          defaultType={defaultType}
        />
      )}
    />
  );
};
